










import { Component, Vue, Emit } from 'vue-property-decorator';
import GSTC, { Columns, Config, GSTCResult, htmlResult, Item, Items, Row, Rows, Vido } from 'gantt-schedule-timeline-calendar';
import "gantt-schedule-timeline-calendar/dist/style.css";
import { Plugin as Bookmarks } from "gantt-schedule-timeline-calendar/dist/plugins/time-bookmarks.esm.min.js";
import { Plugin as HighlightWeekends } from 'gantt-schedule-timeline-calendar/dist/plugins/highlight-weekends.esm.min.js';
import { Plugin as ItemMovement } from 'gantt-schedule-timeline-calendar/dist/plugins/item-movement.esm.min.js';
import { Plugin as ItemResizing } from 'gantt-schedule-timeline-calendar/dist/plugins/item-resizing.esm.min.js';
import { Plugin as Selection } from 'gantt-schedule-timeline-calendar/dist/plugins/selection.esm.min.js';
import { Plugin as TimelinePointer } from 'gantt-schedule-timeline-calendar/dist/plugins/timeline-pointer.esm.min.js';
import { BeforeAfterInitialItems, Events, OnArg, Options as ItemResizingOptions, SnapToTime } from 'gantt-schedule-timeline-calendar/dist/plugins/item-resizing';
import { AutoScroll, Options as ItemMovementOptions, Threshold } from 'gantt-schedule-timeline-calendar/dist/plugins/item-movement';
import { Options as SelectionOptions } from 'gantt-schedule-timeline-calendar/dist/plugins/selection';
import DeepState from 'deep-state-observer';
import { Properties as CSSProps } from 'csstype';
import dayjs from 'dayjs';
import { vxm } from '@/store';
import { TaskDetailParam } from '@/@types/LibraryComponent';
import { UpdateTaskParam } from '@/@types/Task';

/**
 * ライブラリ定義の型を当て嵌めるとtypeErrorになるので、"{[key:string]:string}"を定義してindex signatureに適用する
 * 元の型に含まれる[key:string]と同一レイヤーのプロパティについては必要な分だけユニオン型で増やして再現するしかないと思われる
 */
interface ChartRow {
  [key: string]: {
    taskName: string;
    responsible: string;
  }
  | string
  | boolean
  | number;
}

interface ChartItem {
  [key: string]: {
    id: string;
    label: string;
    rowId: string;
    time: {
      start: number;
      end: number;
    };
    style?: CSSProps;
  }
}

interface HandleContentObject {
  left: htmlResult; // template like: GSTC.api.lithtml.html`<div>content?</div>`
  right: htmlResult; // template like: GSTC.api.lithtml.html`<div>content?</div>`
}

interface ItemLabel {
  item: Item;
  vido: Vido;
}

type HandleContentFunction = ({ item, vido }: ItemLabel ) => HandleContentObject;

type HandleContent = HandleContentObject | HandleContentFunction | htmlResult;

/**
 * テーブル用の情報を定数で定義
 */
const TableInfo = {
  header: {
    taskName: '課題名',
    responsible: '担当者'
  },
  column: {
    taskName: 'taskName',
    responsible: 'responsible',
  },
} as const;

/**
 * gantt-schedule-timeline-calendarのデフォルトオプションについては以下を参照
 * {@link https://gantt-schedule-timeline-calendar.neuronet.io/documentation/default-options}
 * Configuration stateについては以下を参照
 * {@link https://gantt-schedule-timeline-calendar.neuronet.io/documentation/state}
 */
@Component
export default class GanttChartViewBase extends Vue {
  private gstc: GSTCResult | null = null;
  private state: DeepState | null = null;
  /**
   * ガントチャートの表示全体に関わる設定用変数
   * 表示するデータと、ほぼ全ての設定はこのConfigオブジェクトにセットする
   */
  private config: Config | null = null;
  /**
   * テーブルのカラムを構成するデータ
   * プロパティ定義についてはモジュールパス以下のdist/gstc.d.tsを参照
   */
  private columns: Columns = {
    data: {
      taskName: {
        id: TableInfo.column.taskName,
        width: 300,
        data: ({ row }: { row: Row }) => GSTC.api.sourceID(row.taskName),
        header: {
          content: TableInfo.header.taskName,
        },
      },
      responsible: {
        id: TableInfo.column.responsible,
        width: 120,
        data: ({ row }: { row: Row }) => GSTC.api.sourceID(row.responsible),
        header: {
          content: TableInfo.header.responsible,
        },
      },
    },
  };
  /**
   * とりあえず定義したチャート内タスク用のカラーコード
   * 動的にカラーをひたすら作る関数を定義するのは結構難しいので、このように固定値を定義した上で循環させるのが無難と思う
   */
  private colors: string[] = [
    '#7fbfff',
    '#ff7fbf',
    '#7f7fff',
    '#ff7f7f',
    '#ffbf7f',
    '#7fffbf',
    '#7fffff',
    '#bfff7f',
  ];
  private events: Events | null = null;
  private snapToTime: SnapToTime | null = null;
  private handleContent: HandleContent | null = null;
  private itemResizingOptions: ItemResizingOptions | null = null;

  private canChangeRow = false;
  private threshold: Threshold | null = null;
  private autoScroll: AutoScroll | null = null;
  private itemMovementOptions: ItemMovementOptions | null = null;

  //TODO: selection plugin用のoptionsは課題と関係無いので後で消す(シフト機能用)
  private selectionOptions: SelectionOptions | null = null;

  private created() {
    const self = this;
    this.events = {
      /**リサイズevent開始時のハンドラー */
      onStart({ items }: OnArg) {
        return self.handleResizedFrozenItems(items);
      },
      /**リサイズevent発動中のハンドラー */
      onResize({ items }: OnArg) {
        return self.filterFrozenItems(items.after);
      },
      /**リサイズevent終了時のハンドラー */
      onEnd({ items }: OnArg) {
        return self.handleResizedFrozenItems(items);
      },
    };
    this.snapToTime = {
      start({ startTime, time }) {
        return startTime.startOf(time.period);
      },
      end({ endTime, time }) {
        return endTime.endOf(time.period);
      },
    };
    this.handleContent = ({ item, vido }) => ({
      left: vido.html`<div class="resizing-handler-content-left">!</div>`,
      right: vido.html`<div class="resizing-handler-content-right">?</div>`,
    });
    this.itemResizingOptions = {
      events: this.events,
      snapToTime: this.snapToTime,
      content: (this.handleContent) as unknown as htmlResult,
    };

    this.threshold = {
      horizontal: 10,
      vertical: 10,
    };
    this.autoScroll = {
      speed: {
        horizontal: 1,
        vertical: 1,
      },
      edgeThreshold: {
        horizontal: 50,
        vertical: 0,
      },
    };
    this.itemMovementOptions = {
      autoScroll: this.autoScroll,
      threshold: this.threshold,
      events: {
        onMove({ items }) {
          // items before the current movement
          return items.before.map((beforeMovementItem, index) => {
            // item data after move
            const afterMovementItem = items.after[index];
            // clone item to prevent bugs
            const myItem = GSTC.api.merge({}, afterMovementItem) as Item;
            // prevent items to change row if needed
            if (!self.canChangeRow) {
              myItem.rowId = beforeMovementItem.rowId;
            }
            // return modified item (or not if we don't want to move it -> return beforeMovementItem;)
            return myItem;
          });
        },
        onEnd({ items }) {
          // items before the current movement
          const movementItems = items.before.map((beforeMovementItem, index) => {
            // item data after move
            const afterMovementItem = items.after[index];
            // clone item to prevent bugs
            const myItem = GSTC.api.merge({}, afterMovementItem) as Item;
            // prevent items to change row if needed
            if (!self.canChangeRow) {
              myItem.rowId = beforeMovementItem.rowId;
            }
            // return modified item (or not if we don't want to move it -> return beforeMovementItem;)
            return myItem;
          });
          self.handleMovedFrozenItems(items);
          return movementItems;
        },
      },
      snapToTime: {
        start({ startTime, time }) {
          // snap item start time to 12:00 AM while moving
          return startTime.startOf("day").add(12, "hour");
        },
      },
    };

    const ITEM = 'chart-timeline-items-row-item';
    const CELL = 'chart-timeline-grid-row-cell';

    /**特定のセル（現在の日付より前など）を除外する場合はselecting[CELL]に対してfilterをかける */
    this.selectionOptions = {
      events: {
        onStart(lastSelected) {
        },
        onSelecting(selecting, last) {
          return selecting;
        },
        onEnd(selected) {
          console.log("Selection ends", selected[CELL]);
          return selected;
        },
      },
    }

    this.config = {
      licenseKey:
        "====BEGIN LICENSE KEY====\nXOfH/lnVASM6et4Co473t9jPIvhmQ/l0X3Ewog30VudX6GVkOB0n3oDx42NtADJ8HjYrhfXKSNu5EMRb5KzCLvMt/pu7xugjbvpyI1glE7Ha6E5VZwRpb4AC8T1KBF67FKAgaI7YFeOtPFROSCKrW5la38jbE5fo+q2N6wAfEti8la2ie6/7U2V+SdJPqkm/mLY/JBHdvDHoUduwe4zgqBUYLTNUgX6aKdlhpZPuHfj2SMeB/tcTJfH48rN1mgGkNkAT9ovROwI7ReLrdlHrHmJ1UwZZnAfxAC3ftIjgTEHsd/f+JrjW6t+kL6Ef1tT1eQ2DPFLJlhluTD91AsZMUg==||U2FsdGVkX1/SWWqU9YmxtM0T6Nm5mClKwqTaoF9wgZd9rNw2xs4hnY8Ilv8DZtFyNt92xym3eB6WA605N5llLm0D68EQtU9ci1rTEDopZ1ODzcqtTVSoFEloNPFSfW6LTIC9+2LSVBeeHXoLEQiLYHWihHu10Xll3KsH9iBObDACDm1PT7IV4uWvNpNeuKJc\npY3C5SG+3sHRX1aeMnHlKLhaIsOdw2IexjvMqocVpfRpX4wnsabNA0VJ3k95zUPS3vTtSegeDhwbl6j+/FZcGk9i+gAy6LuetlKuARjPYn2LH5Be3Ah+ggSBPlxf3JW9rtWNdUoFByHTcFlhzlU9HnpnBUrgcVMhCQ7SAjN9h2NMGmCr10Rn4OE0WtelNqYVig7KmENaPvFT+k2I0cYZ4KWwxxsQNKbjEAxJxrzK4HkaczCvyQbzj4Ppxx/0q+Cns44OeyWcwYD/vSaJm4Kptwpr+L4y5BoSO/WeqhSUQQ85nvOhtE0pSH/ZXYo3pqjPdQRfNm6NFeBl2lwTmZUEuw==\n====END LICENSE KEY====",
      plugins: [
        TimelinePointer(),
        Selection(this.selectionOptions),
        ItemResizing(this.itemResizingOptions),
        ItemMovement(this.itemMovementOptions),
        Bookmarks(),
        HighlightWeekends({
          weekdays: [6, 0],
          className: 'highlighted-days',
        }),
      ],
      headerHeight: 100,
      list: {
        columns: this.columns,
        rows: this.generateRows(),
      },
      chart: {
        items: this.generateItems(),
        grid: {},
        item: {
          gap: {
            top: 4,
            bottom: 4,
          },
          height: 30 - 3,
          minWidth: 10,
          cutIcons: {
            left:
              '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" style="-ms-transform: rotate(360deg); -webkit-transform: rotate(360deg); transform: rotate(360deg);" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M20 9v6h-8v4.84L4.16 12L12 4.16V9h8z" fill="#ffffff"/><rect x="0" y="0" width="24" height="24" fill="rgba(0, 0, 0, 0)" /></svg>',
            right:
              '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" focusable="false" width="1em" height="1em" style="-ms-transform: rotate(360deg); -webkit-transform: rotate(360deg); transform: rotate(360deg);" preserveAspectRatio="xMidYMid meet" viewBox="0 0 24 24"><path d="M4 15V9h8V4.16L19.84 12L12 19.84V15H4z" fill="#ffffff"/><rect x="0" y="0" width="24" height="24" fill="rgba(0, 0, 0, 0)" /></svg>',
          },
        },
        time: {
          period: 'days',
          from: 0,
          to: 0,
          zoom: 20,
          leftGlobal: 0,
          rightGlobal: 0,
          calculatedZoomMode: false,
          onLevelDates: [],
          onCurrentViewLevelDates: [],
          onDate: [],
          allDates: [],
        },
      },
    };
  }

  private beforeMount() {
    if (this.gstc) {
      this.gstc.destroy();
    }
  }

  private mounted() {
    if (this.config) {
      this.state = GSTC.api.stateFromConfig(this.config);
      this.gstc = GSTC({
        element: this.$refs.gstc as HTMLElement,
        state: this.state,
      });
    }
  }

  /**
   * テーブルrowの生成
   */
  private generateRows(): Row {
    const rows: ChartRow = {};
    const tasks = this.tasks;
    if (tasks) {
      tasks.forEach((task) => {
        const id = task.taskId.toString();
        rows[id] = {
          taskName: task.taskName,
          responsible: task.currentResponsiblePerson ?
            task.currentResponsiblePerson.accountName :
            '担当者なし',
        };
      });
    }
    return rows as Row;
  }

  /**
   * チャートitemの生成
   * 開始日時・終了日時はUNIXタイムスタンプmilliseconds形式で指定する
   */
  private generateItems() {
    const items: ChartItem = {};
    const tasks = this.tasks;
    if (tasks) {
      let colorSelectIndex = 0;
      tasks.forEach((task) => {
        if (!task.startDate && !task.deadline) {
          return;
        }
        let startDate: number | null;
        let deadline: number | null;
        if (!task.startDate) {
          const unixTime = dayjs(task.deadline as Date).valueOf();
          startDate = unixTime;
          deadline = unixTime;
        } else if (!task.deadline) {
          const unixTime = dayjs(task.startDate as Date).valueOf();
          startDate = unixTime;
          deadline = unixTime;
        } else {
          startDate = dayjs(task.startDate as Date).valueOf();
          deadline = dayjs(task.deadline as Date).valueOf();
        }
        const id = task.taskId.toString();
        items[id] = {
          id: id,
          label: task.taskName,
          rowId: id,
          time: {
            start: startDate,
            end: deadline,
          },
          style: { background: this.colors[colorSelectIndex], opacity: '0.8' },
        };
        if (colorSelectIndex === this.colors.length - 1) {
          colorSelectIndex = 0;
        } else {
          ++colorSelectIndex;
        }
      });
    }
    return items as Items;
  }

  /**チャートデータ更新用 */
  private updateChartData(rows: Rows, items: Items) {
    this.state?.update(`config.list.rows`, rows);
    this.state?.update(`config.chart.items`, items);
  }

  /**アイテムリサイズ時のハンドリング用関数 */
  private filterFrozenItems(items: Item[]) {
    return items.filter((item) => {
      if (item.frozen) return false;
      return true;
    });
  }

  private handleResizedFrozenItems(
    { initial, after }: BeforeAfterInitialItems
  ) {
    if (
      (initial.length !== 1 && after.length !== 1) ||
      (initial[0].time.start === after[0].time.start &&
        initial[0].time.end === after[0].time.end
      )
    ) {
      return after;
    }
    const taskId = parseInt(initial[0].id, 10);
    //TODO UTC文字列に変換(現行のAPI設計対応)
    if (initial[0].time.start === after[0].time.start) {
      const deadline = dayjs(after[0].time.end).format('YYYY-MM-DD');
      this.emitTaskData(taskId, deadline, 'deadline');
    } else {
      const startDate = dayjs(after[0].time.start).format('YYYY-MM-DD');
      this.emitTaskData(taskId, startDate, 'startDate');
    }
    return after;
  }

  private handleMovedFrozenItems(
    { initial, after }: BeforeAfterInitialItems
  ) {
    if (
      (initial.length !== 1 && after.length !== 1) ||
      (initial[0].time.start === after[0].time.start &&
        initial[0].time.end === after[0].time.end
      )
    ) {
      return after;
    }
    const taskId = parseInt(initial[0].id, 10);
    //TODO UTC文字列に変換(現行のAPI設計対応)
    const startDate = dayjs(after[0].time.start).format('YYYY-MM-DD');
    const deadline = dayjs(after[0].time.end).format('YYYY-MM-DD');
    this.emitMovedChartItem(taskId, startDate, deadline);
    return after;
  }

  /**
   * テーブルrow更新テスト用関数
   * リアクティブな更新を実装するまではこちらを使用して下さい。
   */
  private updateTest() {
    const rows = this.generateRows();
    const items = this.generateItems();
    this.updateChartData(rows, items);
  }

  /**
   * カレンダズームレベルテスト関数
   * htmlにテストを埋め込んでいるのでそれで確認して下さい。
   */
  private changeZoomLevel() {
    this.state?.update("config.chart.time.zoom", 21);
  }

  private get tasks() {
    return vxm.theoryApi.tasksStore.getTasks?.results;
  }

  @Emit('emitTaskData')
  private emitTaskData(taskId: number, date: string, attributeKey: string) {
    const emitData: TaskDetailParam = {
      taskId: taskId,
      value: date,
      attributeKey: attributeKey,
      customFlag: false,
    };
    return emitData;
  }

  @Emit('emitMovedChartItem')
  private emitMovedChartItem(taskId: number, startDate: string, deadline: string) {
    const emitData: UpdateTaskParam = {
      startDate: startDate,
      deadline: deadline,
    };
    return {
      id: taskId,
      param: emitData,
    };
  }
}
